"use client";
import React from "react";
import { useEffect, useState } from "react";
import { ResumeForm } from "components/ResumeForm";
import dynamic from "next/dynamic";
const Resume = dynamic(() =>
  import("components/Resume").then((m) => (m as any).Resume as React.ComponentType<any>)
  , {
  ssr: false,
  loading: () => (
   //skeleton
    <div className="mt-5 flex h-full flex-col">
      <div className="flex h-[calc(100vh-160px)] justify-center overflow-y-scroll bg-white px-5 scrollbar">
        {/* Centered loading spinner */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            {/* <div className="h-16 w-16 animate-spin rounded-full border-4 border-gray-300 border-t-blue-500" /> */}
            <div className="w-24 h-24 rounded-full bg-gray-200 animate-pulse" />
          </div>
        </div>
      </div>
      {/* ProgressBar placeholder */}
      <div className="h-[40px]" />
    </div>
  ),
});
import {
  selectIsImportComponentOpen,
  selectIsSidebarOpen,
  selectSettings,
  setCurrentSection,
} from "lib/redux/settingsSlice";
import {
  changeThemeColor,
  changeFontFamily,
  changeFontSize,
  changeDocumentSize,
  changeFormToShow,
  changeFormToHeading,
  changeFormsOrder,
  selectResume,
  changePublishedStatus,
  changeLeftRightFormOrder,
} from "lib/redux/resumeSlice";
import { ImportResumeComponent } from "./ImportResumeComponent";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { BuilderTopBar } from "./BuilderTopBar"; 
import { AuthenticationOptions } from "./AuthenticationOptions";
import { ResumePublishOptions } from "./ResumePublishOptions";
import { ToastContainer, toast } from "react-toastify";
import { changeResumeVisibility } from "api/resumeApi";
import { MobileResumeOverlay } from "./MobileResumeOverlay";
import MobileProgressButton from "./MobileProgressButton";
import { SaveResumePopup } from "./SaveResumePopup";
import {
  selectIsAuthenticationOpen,
  selectIsExplorePlansOverlayOpen,
  selectOpenMobileResumeOverlay,
  selectUiSettings,
  setIsExplorePlansOverlayOpen,
  setIsPremiumUser,
  setOpenMobileResumeOverlay,
  setSubscriptionCreditCount,
} from "lib/redux/uiSlice";
import MobileResumeTemplates from "./Mobile/MobileResumeTemplates";
import { MobileThemeForm } from "./Mobile/ThemeForm";
import { getSubscriptionInfo } from "api/subscription/subscriptionInfo";
import { ExplorePlansOverlay } from "./OverlaysAndPopups/ExplorePlansOverlay";
import { fetchCountryCode } from "lib/utils/getCountryCode";
import { FontPreloaderWithWait } from "components/fonts/FontPreloaderWithWait";
import { ENGLISH_FONT_FAMILIES } from "components/fonts/constants";

const ResumeBuilderComponent = () => {
  const isImportComponentOpen = useAppSelector(selectIsImportComponentOpen);
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const isAuthenticationOpen = useAppSelector(selectIsAuthenticationOpen);
  const isExplorePlansOverlayOpen = useAppSelector(
    selectIsExplorePlansOverlayOpen
  );
  const [openShareResumeModal, setOpenShareResumeModal] = useState(false);
  const openMobileResumeOverlay = useAppSelector(selectOpenMobileResumeOverlay);
  const [openSaveResumePopup, setOpenSaveResumePopup] = useState(false);
  const dispatch = useAppDispatch();
  const resume = useAppSelector(selectResume);
  const settings = useAppSelector(selectSettings);
  const ui = useAppSelector(selectUiSettings);
  const [openTemplatesPreview, setOpenTemplatesPreview] = useState(false);
  const [openMobileThemeForm, setOpenMobileThemeForm] = useState(false);
  const [countryCode, setCountryCode] = useState("IN");
  
  // Prevent layout shift by stabilizing initial render
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Resume-builder page ONLY: Wait for fonts to prevent CLS
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    fetchCountryCode(setCountryCode);
  }, []);

  // Always start with "templates" section on page load (not from localStorage)
  useEffect(() => {
    dispatch(setCurrentSection("templates"));
  }, [dispatch]);

  // Load react-toastify CSS at runtime by injecting a <link> to a CDN-hosted stylesheet.
  // This avoids bundler/type errors from importing the CSS module while keeping the
  // CSS non-blocking for the initial bundle.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const id = "react-toastify-css";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/react-toastify/dist/ReactToastify.css";
    link.crossOrigin = "anonymous";
    link.onload = () => {
      // stylesheet loaded
    };
    link.onerror = () => {
      // ignore failures; toast styles are optional
    };
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    dispatch(changeThemeColor(settings.themeColor));
    dispatch(changeFontFamily(settings.fontFamily));
    dispatch(changeFontSize(settings.fontSize));
    dispatch(changeDocumentSize(settings.documentSize));
    dispatch(changeFormToShow(settings.formToShow));
    dispatch(changeFormToHeading(settings.formToHeading));
    dispatch(changeFormsOrder(settings.formsOrder));
    dispatch(changeLeftRightFormOrder(settings?.leftRightFormOrder!));
    
    // Mark initial load complete after settings are applied
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [settings, ui.userProfilePhoto]);

  useEffect(() => {
    const accessToken = localStorage.getItem("access-token");

    dispatch(setIsExplorePlansOverlayOpen(false));
    try {
      const subscriptionInfo = async () => {
        const tokenCount = await getSubscriptionInfo();
        dispatch(setSubscriptionCreditCount(tokenCount.credits));
        dispatch(setIsPremiumUser(tokenCount.premium));
      };
      // Call API to get subscription info only if the user is logged in
      if (accessToken) {
        subscriptionInfo();
      }
    } catch (error) {
      console.error("Error getting subscription info:", error);
    }
  }, []);

  const handleChangeResumeVisibility = async (
    resumeId: number,
    visibility: boolean
  ) => {
    try {
      // Call API to change visibility
      await changeResumeVisibility(resumeId, {
        published: visibility,
      });
      dispatch(changePublishedStatus(visibility));
    } catch (error) {
      toast.error("Error updating resume visibility");
    }
  };

  return (
    <main className="relative h-screen w-full overflow-hidden bg-[#F1F1F1]">
      {/*Preload fonts (Regular+Bold) to prevent CLS */}
     <FontPreloaderWithWait 
        fontFamilies={ENGLISH_FONT_FAMILIES}
        waitForLoad={true}
        onFontsLoaded={() => setFontsReady(true)}
      />
      
      <BuilderTopBar
        setOpenShareResumeModal={setOpenShareResumeModal}
        setOpenSaveResumePopup={setOpenSaveResumePopup}
      />
      <div className="mx-4 flex h-[calc(100vh-62px)] lg:mx-8" style={{ contentVisibility: 'auto' }}>
        <div
          className={`w-full transition-all duration-500 ${isSidebarOpen ? "lg:w-[66%]" : "lg:w-[50%]"
            }`}
          style={{ willChange: 'width' }}
        >
          <ResumeForm />
        </div>
        <div className="hidden max-w-[50%] flex-1 lg:block" style={{ contain: 'layout' }}>
          {fontsReady ? (
            <Resume />
          ) : (
           
            <div className="mt-5 flex h-full flex-col">
              <div className="flex h-[calc(100vh-160px)] justify-center overflow-y-scroll bg-white px-5 scrollbar">
                {/* Centered loading spinner */}
                <div className="flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    {/* <div className="h-16 w-16 animate-spin rounded-full border-4 border-gray-300 border-t-blue-500" /> */}
                    <div className="w-24 h-24 rounded-full bg-gray-200 animate-pulse" />
                  </div>
                </div>
              </div>
              {/* ProgressBar placeholder */}
              <div className="h-[40px]" />
            </div>
          )}
        </div>
      </div>
      {isImportComponentOpen && (
        <div className="absolute left-1/2 top-0 z-10 w-fit -translate-x-1/2 bg-gray-400 bg-opacity-25">
          <ImportResumeComponent />
        </div>
      )}

      {/* Render Authentication Modal */}
      {isAuthenticationOpen && <AuthenticationOptions />}

      {
        // Render Save Resume Popup
        openSaveResumePopup && (
          <SaveResumePopup onClose={() => setOpenSaveResumePopup(false)} />
        )
      }

      {
        // Render Share Resume Modal
        openShareResumeModal && (
          <ResumePublishOptions
            onClose={() => setOpenShareResumeModal(false)}
            resumeId={resume.id!}
            handleChangeResumeVisibility={handleChangeResumeVisibility}
            published={resume.published!}
          />
        )
      }
      {openMobileResumeOverlay && (
        <MobileResumeOverlay
          setOpenShareResumeModal={setOpenShareResumeModal}
          setOpenSaveResumePopup={setOpenSaveResumePopup}
          setOpenTemplatesPreview={setOpenTemplatesPreview}
          setOpenMobileThemeForm={setOpenMobileThemeForm}
        />
      )}
      {openTemplatesPreview && (
        <MobileResumeTemplates
          setOpenTemplatesPreview={setOpenTemplatesPreview}
        />
      )}
      {
        // Render Theme Form
        openMobileThemeForm && (
          <MobileThemeForm setOpenMobileThemeForm={setOpenMobileThemeForm} />
        )
      }
      <div
        className="absolute bottom-20 right-4 z-40"
        onClick={() => dispatch(setOpenMobileResumeOverlay(true))}
      >
        <MobileProgressButton />
      </div>

      {isExplorePlansOverlayOpen && (
        <ExplorePlansOverlay countryCode={countryCode} />
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </main>
  );
};

export default ResumeBuilderComponent;
